import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './components/providers/theme-provider';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/confirm-dialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { MainLayout } from './components/layout/MainLayout';
import { RequireAuth } from './features/auth/components/RequireAuth';
import { AuthInitializer } from './components/AuthInitializer';
import {
  noragBookRoles,
  noragBotRoles,
  noragReadRoles,
  noragTemplateRoles,
} from './features/auth/rolePermissions';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PerfilPage = lazy(() =>
  import('./pages/PerfilPage').then(module => ({ default: module.PerfilPage }))
);
const EmpresasPage = lazy(() => import('./pages/EmpresasPage'));
const NoragDomainsPage = lazy(() => import('./pages/NoragDomainsPage'));
const NoragDomainDetailPage = lazy(() => import('./pages/NoragDomainDetailPage'));
const NoragTemplatesPage = lazy(() => import('./pages/NoragTemplatesPage'));
const NoragStatsPage = lazy(() => import('./pages/NoragStatsPage'));
const NoragBooksPage = lazy(() => import('./pages/NoragBooksPage'));
const NoragBotsPage = lazy(() => import('./pages/NoragBotsPage'));
const NoragExpedientesPage = lazy(() => import('./pages/NoragExpedientesPage'));
const NoragCopilotPage = lazy(() => import('./pages/NoragCopilotPage'));

function App() {
  return (
    <ThemeProvider defaultTheme='light' storageKey='base-de-conocimiento-theme'>
      <ErrorBoundary>
        <AuthInitializer>
          <ToastProvider>
            <ConfirmProvider>
              <Suspense fallback={<LoadingSpinner className='min-h-screen' />}>
                <Routes>
                  <Route path='/login' element={<LoginPage />} />
                  <Route element={<MainLayout />}>
                    <Route element={<RequireAuth />}>
                      <Route path='/' element={<DashboardPage />} />
                      <Route path='/perfil' element={<PerfilPage />} />
                      <Route
                        element={<RequireAuth allowedRoles={['SUPERADMIN']} />}
                      >
                        <Route path='/empresas' element={<EmpresasPage />} />
                      </Route>
                      <Route
                        element={
                          <RequireAuth allowedRoles={noragReadRoles} />
                        }
                      >
                        <Route path='/norag/dominios' element={<NoragDomainsPage />} />
                        <Route path='/norag/dominios/:slug' element={<NoragDomainDetailPage />} />
                        <Route path='/norag/expedientes' element={<NoragExpedientesPage />} />
                        <Route path='/norag/copilot' element={<NoragCopilotPage />} />
                        <Route path='/norag/metricas' element={<NoragStatsPage />} />
                      </Route>
                      <Route element={<RequireAuth allowedRoles={noragTemplateRoles} />}>
                        <Route path='/norag/plantillas' element={<NoragTemplatesPage />} />
                      </Route>
                      <Route
                        element={
                          <RequireAuth allowedRoles={noragBookRoles} />
                        }
                      >
                        <Route path='/norag/libros' element={<NoragBooksPage />} />
                      </Route>
                      <Route
                        element={
                          <RequireAuth allowedRoles={noragBotRoles} />
                        }
                      >
                        <Route path='/norag/bots' element={<NoragBotsPage />} />
                      </Route>
                    </Route>
                  </Route>
                  <Route path='*' element={<Navigate to='/' replace />} />
                </Routes>
              </Suspense>
            </ConfirmProvider>
          </ToastProvider>
        </AuthInitializer>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
